import React, { useState, useEffect } from 'react';
import SelectModuleTemplate from './SelectModuleTemplate';
import StandardInputField from '../Shared/StandardInputField';
import Button from '@material-ui/core/Button';
import ModuleInfo from './ModuleInfo';
import styles from './UpsertModule.module.css';
import { getModuleTemplates, postModule } from '../../api/moduleService';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Notify, Confirm } from 'notiflix';
import { useTokenCheck } from '../../hooks/useTokenCheck';

/**
 * Adds or edits module information
 * @param isNew whether or not this is a new module. If false, we edit the one stored in redux
 * @returns {JSX.Element}
 */
const UpsertModule = ({ isNew = true }) => {
  useTokenCheck();
  const { t } = useTranslation();
  const history = useHistory();
  const selectedModule = useSelector(
    (state) => state.operationalDataReducer.selectedModule
  );
  const dispatch = useDispatch();
  const moduleTemplates = useSelector(
    (state) => state.moduleReducer.moduleTemplates
  );
  const [moduleTemplate, setModuleTemplate] = useState();
  const [serialNumber, setSerialNumber] = useState();
  const [mileage, setMileage] = useState();
  const [productionYear, setProductionYear] = useState();
  const [dateOfRetirement, setDateOfRetirement] = useState();
  const [retirementReason, setRetirementReason] = useState();
  const [chargingCycles, setChargingCycles] = useState();
  const [events, setEvents] = useState();
  const [cabinetId, setCabinetId] = useState();

  useEffect(() => {
    if (!isNew) {
      setModuleTemplate(selectedModule.template);
      setSerialNumber(selectedModule.serialNumber);
      setMileage(selectedModule.mileage);
      setProductionYear(selectedModule.productionYear);
      setRetirementReason(selectedModule.retirement);
      setChargingCycles(selectedModule.chargingCycles);
      setEvents(selectedModule.events);
      setCabinetId(selectedModule.cabinetId);
    }

    dispatch(getModuleTemplates());
  }, [isNew]);

  const handleSubmitButtonClicked = (e) => {
    e.preventDefault();
    const moduleData = {
      productionYear,
      serialNumber,
      mileage,
      chargingCycles,
      events,
      retirement: retirementReason,
      cabinetId,
      moduleTemplateId: moduleTemplate.id
    };

    if (!isNew) {
      moduleData.id = selectedModule.id;
    }

    postModule(moduleData)
      .then(() => Notify.success('Module ' + (isNew ? 'added! ' : 'updated!')))
      .catch((err) => {
        Notify.failure('Something went wrong, please try again');
        console.error(err);
      });
    setSerialNumber('');
    setMileage('');
    setProductionYear('');
    setDateOfRetirement('');
    setRetirementReason('');
    setChargingCycles('');
    setEvents('');
    setCabinetId('');

    history.push('/modulelibrary');
  };

  return (
    <div className={styles.container}>
      <h1>{isNew ? t('addNewModule') : t('updateModule')}</h1>
      <div className={styles.content}>
        <div className={styles.moduleTemplateContainer}>
          <h2 className={styles.subHeader}>{t('ModuleType')}</h2>
          <SelectModuleTemplate
            moduleTemplates={moduleTemplates}
            moduleTemplate={moduleTemplate}
            setModuleTemplate={setModuleTemplate}
            className={styles.upsertModuleInput}
          />
          <ModuleInfo moduleTemplate={moduleTemplate} />
        </div>
        <div>
          <h2 className={styles.subHeader}>{t('ModuleInformation')}</h2>
          <form
            className={styles.upsertModuleFormContainer}
            onSubmit={handleSubmitButtonClicked}
          >
            <div className={styles.inputFormGrid}>
              <StandardInputField
                label={t('serialNumber')}
                handleChange={setSerialNumber}
                value={serialNumber}
                required
              />
              <StandardInputField
                label={t('Mileage')}
                handleChange={setMileage}
                value={mileage}
              />
              <StandardInputField
                label={t('ProductionYear')}
                handleChange={setProductionYear}
                value={productionYear}
              />
              <StandardInputField
                label={t('DateOfRetirement')}
                handleChange={setDateOfRetirement}
                value={dateOfRetirement}
              />
              <StandardInputField
                label={t('RetirementReason')}
                handleChange={setRetirementReason}
                value={retirementReason}
              />
              <StandardInputField
                label={t('cycles')}
                handleChange={setChargingCycles}
                value={chargingCycles}
              />
              <StandardInputField
                label={t('Events')}
                handleChange={setEvents}
                value={events}
              />
            </div>
            <div className={styles.formButtonsContainer}>
              <Button
                variant={'contained'}
                className={styles.button}
                onClick={() =>
                  Confirm.show(
                    'Are you sure you want to cancel?',
                    'All entered information will be lost',
                    'Yes',
                    'No',
                    () => history.push('/modulelibrary'),
                    () => {}
                  )
                }
              >
                {t('cancel')}
              </Button>
              <Button
                className={styles.button}
                variant={'contained'}
                type="submit"
              >
                {t('finish')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpsertModule;
